import { isEqual } from "lodash";
import { useEffect, useMemo, useRef, useState, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue } from "recoil";

import { useTableColumnsDynamic } from "@ryde/controls";
import {
  useBookingsActions,
  useBookingsNavigation,
  useCustomQueryParams,
  useInitViewsAndFilters,
} from "@ryde/redux";
import {
  bulkCloneFormDrawerAF,
  bulkOperationsDrawerAF,
  bulkUpdateFormDrawerAF,
  controlsSF,
  ICustomField,
  IFilterSchemaObject,
  isBulkBookingSetFinishSelector,
  isNeedToFetchTableRowsAF,
  RydeUiUnit,
  tableFiltersSchemaAtomFamily,
  tableSettingsDrawerAF,
  topCalendarDatesAtomFamily,
} from "@ryde/shared";

const entity: RydeUiUnit = "booking";

const needToFetchPassengersControlNames = new Set([
  "passenger.unitId",
  "passenger.siteId",
  "passengersHomeAreaId",
  "passengersDestinationAreaId",
]);

const checkIsNeedFetchPassengersFromFilters = ({
  controls,
  tableFilters,
}: {
  controls: ICustomField[];
  tableFilters: IFilterSchemaObject | undefined;
}): boolean => {
  if (!tableFilters) {
    return false;
  }

  return Object.keys(tableFilters).some(controlId => {
    const control = controls.find(({ id }) => Number(id) === Number(controlId));

    if (!control) {
      return false;
    }

    return (
      needToFetchPassengersControlNames.has(control.name) &&
      tableFilters[Number(controlId)] !== null
    );
  });
};

export const useBookingsPage = () => {
  const [isPending, startTransition] = useTransition();
  const calendarDates = useRecoilValue(topCalendarDatesAtomFamily(entity));
  const { filtersSchemaFromQs } = useCustomQueryParams();
  const { isCalendarBookingView } = useBookingsNavigation();
  const [isPageReady, setIsPageReady] = useState(false);

  const tableSettingsDrawerOpened = useRecoilValue(
    tableSettingsDrawerAF(entity),
  );
  const bulkOperationsDrawerOpened = useRecoilValue(
    bulkOperationsDrawerAF(entity),
  );
  const bulkUpdateFormDrawerOpened = useRecoilValue(
    bulkUpdateFormDrawerAF(entity),
  );
  const bulkCloneFormDrawerOpened = useRecoilValue(
    bulkCloneFormDrawerAF(entity),
  );
  const { fetchBookingPassengers } = useBookingsActions({
    searchQueryToOptimize: "bookingsPassengers",
  });
  const isBulkBookingSetFinish = useRecoilValue(isBulkBookingSetFinishSelector);
  const { tableDefsGeneric } = useTableColumnsDynamic();
  const [isNeedToFetchTableRows, setIsNeedToFetchTableRows] = useRecoilState(
    isNeedToFetchTableRowsAF(entity),
  );
  const [tableFilters, setTableFilters] = useRecoilState(
    tableFiltersSchemaAtomFamily(entity),
  );

  const actualTableFiltersRef = useRef(tableFilters);
  const tableFilterFinallyInitValueRef = useRef<
    IFilterSchemaObject | undefined
  >(undefined);
  const controls = useRecoilValue(controlsSF(entity));

  const [isInitializing, setIsInitializing] = useState(true);
  const isNeedFetchPassengersFromFilters =
    checkIsNeedFetchPassengersFromFilters({ controls, tableFilters });
  const isSchemaLoaded = controls?.length && controls.length > 0;
  const { t } = useTranslation("general");

  const { fetchTableRowsServerFiltered, isInitPending } =
    useInitViewsAndFilters({
      entity,
      searchQueryToOptimize: "bookings",
      // The schema is fetched by entity name, so it can be ready before the IDs the views request needs.
      isPageDependenciesReady: Boolean(isSchemaLoaded),
      onInitialized: () => setIsPageReady(true),
      onInitSkipped: () => setIsPageReady(false),
    });

  const fetchBookingsAndPassengers = useEvent(() => {
    fetchTableRowsServerFiltered();
    fetchBookingPassengers();
  });
  const onFiltersApply = useEvent((): void => {
    fetchBookingsAndPassengers();
  });
  const bookingHeaderContextValue = useMemo(() => {
    return {
      onFiltersApply: onFiltersApply,
      onFiltersClear: onFiltersApply,
      onSearchApply: onFiltersApply,
    };
  }, [onFiltersApply]);

  useEffect(() => {
    actualTableFiltersRef.current = tableFilters;
  }, [tableFilters]);

  // Drop the filters on unmount, and when the schema changes under the page (company switch), so the next
  // init never starts from another company's filters.
  useEffect(() => {
    return () => {
      setTableFilters(undefined);
    };
  }, [isSchemaLoaded, setTableFilters]);

  useEffect(() => {
    if (
      //filterObjChangesForRefetch &&  => ** DO NOT DELETE - Development under review for active filters on click apply only **
      calendarDates &&
      isSchemaLoaded &&
      isInitializing
    ) {
      setIsInitializing(false);
    }
  }, [
    calendarDates,
    isInitializing,
    isSchemaLoaded /*filterObjChangesForRefetch => ** DO NOT DELETE - Development under review for active filters on click apply only */,
  ]);

  useEffect(() => {
    if (
      tableFilters !== undefined &&
      tableFilterFinallyInitValueRef.current === undefined
    ) {
      tableFilterFinallyInitValueRef.current = tableFilters;
    }
  }, [tableFilters]);

  // Do not add `isNeedToFetchTableRows` to these deps — the effect below owns that flag and this
  // body never reads it. Listing it here fetched the table three times per save.
  useEffect(() => {
    if (isInitializing || !isPageReady) {
      return;
    }

    startTransition(() => {
      setTimeout(() => {
        fetchTableRowsServerFiltered();
      });
    });
  }, [
    calendarDates,
    fetchTableRowsServerFiltered,
    isInitializing,
    isPageReady,
    // filterObjChangesForRefetch,   => ** DO NOT DELETE - Development under review for active filters on click apply only **
  ]);

  useEffect(() => {
    if (isInitializing || !isNeedToFetchTableRows) {
      return;
    }

    setIsNeedToFetchTableRows(false);

    startTransition(() => {
      fetchTableRowsServerFiltered();
    });
  }, [
    fetchTableRowsServerFiltered,
    isNeedToFetchTableRows,
    isInitializing,
    setIsNeedToFetchTableRows,
  ]);

  // initial fetch passengers
  useEffect(() => {
    if (isCalendarBookingView && isSchemaLoaded && !isInitializing) {
      startTransition(() => {
        fetchBookingPassengers();
      });
    }
  }, [
    fetchBookingPassengers,
    isCalendarBookingView,
    isInitializing,
    isSchemaLoaded,
  ]);

  // specific effect for passengers load that triggered by filter changed
  // please keep it separated to avoid such issues RYDE-3999
  useEffect(() => {
    if (isCalendarBookingView && isNeedFetchPassengersFromFilters) {
      startTransition(() => {
        fetchBookingPassengers();
      });
    }
  }, [
    fetchBookingPassengers,
    isCalendarBookingView,
    // filterObjChangesForRefetch,   => ** DO NOT DELETE - Development under review for active filters on click apply only **
    isNeedFetchPassengersFromFilters,
  ]);

  useEffect(() => {
    if (isBulkBookingSetFinish) {
      startTransition(() => {
        fetchTableRowsServerFiltered({ isBypassClearSelection: true });
      });
    }
  }, [fetchTableRowsServerFiltered, isBulkBookingSetFinish]);

  useEffect(() => {
    if (!filtersSchemaFromQs) {
      return;
    }

    if (isEqual(actualTableFiltersRef.current, filtersSchemaFromQs)) {
      return;
    }

    setTableFilters({
      ...actualTableFiltersRef.current,
      ...filtersSchemaFromQs,
    });
  }, [filtersSchemaFromQs, setTableFilters]);

  return {
    mainTitle: t("booking.plural"),
    tableDefsGeneric,
    tableSettingsDrawerOpened,
    bookingHeaderContextValue,
    bulkOperationsDrawerOpened,
    isCalendarBookingView,
    bulkUpdateFormDrawerOpened,
    bulkCloneFormDrawerOpened,
    isPending: isPending || isInitPending,
    pendingText: t("ui.LOADING_DATA"),
  };
};
